import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { requireLogin, syncToken } from '../auth/panor';
import { api } from '../api/client';
import { usePlayerStore } from '../store/player';
import { PixelButton } from '../ui/PixelButton';
import { PixelText } from '../ui/PixelText';
import type { Me } from '../api/client';

function getPostLoginDest(me: Me): string {
  const tutorialDone = me.tutorialCompletedAt != null;
  if (me.roster.length === 0 && !tutorialDone) return '/tutorial';
  return '/';
}

export function Login() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const setMe = usePlayerStore(s => s.setMe);
  const setError = usePlayerStore(s => s.setError);
  const me = usePlayerStore(s => s.me);

  // If App.tsx already restored a session, just route to the post-login destination.
  // (Avoids re-fetching /api/me and racing with App's own restore effect.)
  useEffect(() => {
    if (me) { nav(getPostLoginDest(me), { replace: true }); return; }
    if (syncToken()) {
      api.me.get()
        .then(m => { setMe(m); nav(getPostLoginDest(m), { replace: true }); })
        .catch(() => { /* session expired — stay on login page */ });
    }
  }, [nav, setMe, me]);

  const handleSignIn = async () => {
    try {
      await requireLogin(t('login.subtitle'));
      syncToken();
      const me = await api.me.get();
      setMe(me);
      nav(getPostLoginDest(me));
    } catch (e) {
      setError(String(e));
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-8 p-6">
      <PixelText size="3xl" color="pink">{t('login.title')}</PixelText>
      <PixelText size="base" color="cyan">{t('login.subtitle')}</PixelText>
      <PixelButton onClick={handleSignIn}>{t('login.sign_in_button')}</PixelButton>
      <PixelText size="sm" color="yellow" className="opacity-60">
        {t('login.panor_note')}
      </PixelText>
    </main>
  );
}
